import React from "react";
import { Heart, Calendar, X, Download } from "lucide-react";
import { Photo } from "../../../../types/api";
import LazyImage from "../media/LazyImage";
import { useImageDownload } from "../../../hooks/utils/useImageDownload";

interface PhotoModalProps {
  photo: Photo | null;
  onClose: () => void;
  onLike: (photoId: string) => void;
  className?: string;
}

/**
 * 共通写真モーダルコンポーネント
 * - 写真の詳細表示
 * - いいね機能
 * - ダウンロード機能（PC用）
 * - レスポンシブ対応
 */
const PhotoModal: React.FC<PhotoModalProps> = ({ 
  photo, 
  onClose, 
  onLike, 
  className = "" 
}) => {
  const { downloadImage, isDownloading } = useImageDownload();

  if (!photo) return null;

  const handleDownload = async () => {
    await downloadImage(photo.imageUrl, `photo_${photo.id}.jpg`);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className={`photo-modal-overlay ${className}`} 
      onClick={handleOverlayClick}
    >
      <div className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
        <button 
          className="modal-close-button" 
          onClick={onClose}
          aria-label="モーダルを閉じる"
        >
          <X size={24} />
        </button>
        
        <div className="modal-image-container">
          <LazyImage
            src={photo.imageUrl}
            thumbnailSrc={photo.thumbnailUrl}
            alt="写真"
            className="modal-image"
          />
        </div>
        
        <div className="modal-info">
          <div className="modal-header">
            <div className="photo-date">
              <Calendar className="date-icon" size={16} />
              <span>{new Date(photo.uploadDate).toLocaleDateString('ja-JP')}</span>
            </div>
          </div>
          
          <div className="modal-actions">
            <button 
              className={`like-button ${photo.isLiked ? 'liked' : ''}`}
              onClick={() => onLike(photo.id)}
              aria-label={`${photo.isLiked ? 'いいねを解除' : 'いいねする'}`}
            >
              <Heart size={20} />
              <span>{photo.likes}</span>
            </button>
            
            <button 
              className="action-button" 
              onClick={handleDownload}
              disabled={isDownloading}
              aria-label="画像をダウンロード"
            >
              <Download size={20} />
              <span>{isDownloading ? '保存中...' : '保存'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoModal;